// <purpose>THE bottom bar's model (decide, then step; decision 2026-10-28): three fixed slots on
// every open or decided row, for the letter flow and the triage (WIM) flow alike —
//   slot 1 = the next open act on this mail, else the letter's download, else BLANK (width kept);
//   slot 2 = Neu analysieren;
//   slot 3 = Nicht hilfreich on an open row, Archivieren on a decided one.
// When slot 1 has more than one act to offer (a WIM row with two letters, a Prüfen row's filing,
// the second letter's download), a caret on slot 1 lists them with number keys; a kind with no
// draft is not listed (it appears when its draft arrives), so no disabled sibling stands beside
// the act. The off face that slot 1 wore after a yes without a letter, or after a no, is retired:
// the header's verdict badge says what was decided.</purpose>
// <dependencies>The DraftModel folded once by TicketDetail (the draft area reads the same object),
// the served kind words (draftKindActionLabel / draftKindDownloadLabel). Pure: no React, no
// server; the host maps an act to its mutation.</dependencies>
// <constraints>A slot's face is decided from the row's status, postures and claims alone — never a
// handling id (the Prüfen fact arrives folded as DraftModel.inReview). German strings are the act's
// own verbs, code-owned like Genehmigen; kind words are served.</constraints>
import type { DraftKind, TicketDetail } from '../types';
import type { DraftDecisionKind, DraftModel, DraftSlot, DraftSlotKind } from './draftClaims';
import { draftKindActionLabel, draftKindDownloadLabel, isDraftDecisionKind, openSlotCount, openSlotsNote } from './draftClaims';
import type { FaceFn } from './categoryMeta';
import { isSettledStatus, canRetrySettled } from './settledRow';

export type SlotAct =
  | { type: 'approve' }
  | { type: 'approveKind'; kind: DraftKind }
  | { type: 'download' }
  | { type: 'downloadKind'; kind: DraftDecisionKind }
  | { type: 'retry' }
  | { type: 'archive' };

export interface SlotItem {
  label: string;
  act: SlotAct;
  icon: 'check' | 'retry' | null;
  enabled: boolean;
  /** Why it cannot be pressed (or what it needs) — read out of the button's title. */
  title?: string;
  /** `potential` = offered but not composed yet (washed face, unpressable). */
  face?: 'on' | 'potential';
  /** The number key inside the caret's list. */
  hotkey?: string;
  /** Refused, but not yet LOOKING refused: a draft action's hold, inside its first 250 ms (walk fix
   *  round 4, 2026-09-09 — a fast round trip must not wash the bar out and back in). */
  undimmed?: boolean;
}

export type BarSlot =
  | { type: 'act'; main: SlotItem; menu: SlotItem[] | null; menuHeading?: string }
  | { type: 'blank' }
  | { type: 'decline'; noDraft: boolean };

export type BarSlots = [BarSlot, BarSlot, BarSlot];

// Why a slot is unpressable, in the clerk's words (the bar's shared idiom: the reason in the title).
export const BAR_REASON = {
  notRendered: 'Entwurf zuerst erzeugen',
  noDraft: 'Kein Entwurf vorhanden — Neu analysieren',
  noDraftLetter: 'Kein Entwurf vorhanden — direkte Bearbeitung erforderlich',
  maintenance: 'Wartungsarbeiten — Aktionen pausiert',
  noRetry: 'Bereits versendet.',
  busy: 'Aktion läuft',
} as const;

const RETRY_LABEL = 'Neu analysieren';
const ARCHIVE_LABEL = 'Archivieren';
const LETTER_APPROVE_LABEL = 'Genehmigen';
const LETTER_DOWNLOAD_LABEL = 'Entwurf herunterladen';
/** The caret list's heading when every entry is an approval. */
export const MENU_HEADING_APPROVE = 'Genehmigen';

const retrySlot = (status: string, blocked: boolean): BarSlot => {
  const off = isSettledStatus(status) && !canRetrySettled(status);
  return {
    type: 'act', menu: null,
    main: { label: RETRY_LABEL, act: { type: 'retry' }, icon: 'retry', enabled: !off && !blocked, title: off ? BAR_REASON.noRetry : undefined },
  };
};
const archiveSlot = (canArchive: boolean, blocked: boolean): BarSlot => ({
  type: 'act', menu: null,
  main: { label: ARCHIVE_LABEL, act: { type: 'archive' }, icon: null, enabled: canArchive && !blocked },
});

// ─── The letter flow ──────────────────────────────────────────────────────────────────────────

export interface LetterBarInput {
  status: string;
  /** Genehmigen may be pressed (open, no open fields, a letter or a filing proposal, not triage). */
  canApprove: boolean;
  /** The pipeline composed no letter and proposes no filing — Genehmigen says why it cannot. */
  noDraft: boolean;
  /** Fields of a scaffold letter still open (0 = none). */
  openFields: number;
  /** A settled yes that yielded a letter — slot 1 offers its download. */
  approvedLetter: boolean;
  canArchive: boolean;
  /** The host's writes are on the wire or paused. */
  writesBlocked: boolean;
  maintenance: boolean;
}

/** The letter flow's three slots for an open or decided row; null for any other status. */
export function letterBarSlots(i: LetterBarInput): BarSlots | null {
  if (i.status === 'suggestion_ready') {
    // Open fields are named by the footer's „Felder" pill; the disabled button says it on hover.
    const title = i.maintenance ? BAR_REASON.maintenance : i.noDraft ? BAR_REASON.noDraftLetter
      : i.openFields > 0 ? openSlotsNote(i.openFields) : undefined;
    return [
      {
        type: 'act', menu: null,
        main: { label: LETTER_APPROVE_LABEL, act: { type: 'approve' }, icon: 'check', enabled: i.canApprove && !i.writesBlocked, title },
      },
      retrySlot(i.status, i.writesBlocked),
      { type: 'decline', noDraft: i.noDraft },
    ];
  }
  if (isSettledStatus(i.status)) {
    return [
      i.approvedLetter
        ? { type: 'act', menu: null, main: { label: LETTER_DOWNLOAD_LABEL, act: { type: 'download' }, icon: null, enabled: true } }
        : { type: 'blank' },
      retrySlot(i.status, i.writesBlocked),
      archiveSlot(i.canArchive, i.writesBlocked),
    ];
  }
  return null;
}

// ─── The triage flow ──────────────────────────────────────────────────────────────────────────

/** How many letter slots a triage row has (spec §12.2: no handling declares more). */
const ROW_DRAFT_SLOTS = 2;

/** The decision kinds in the MODEL's order (the cards above the bar stand in the same order):
 *  offered kinds first, then the rest, at most two. */
export const decisionKindsInModelOrder = (model: DraftModel): DraftDecisionKind[] => {
  const kinds = model.slots.map(slot => slot.kind).filter(isDraftDecisionKind);
  const offered = kinds.filter(kind => {
    const slot = model.slots.find(s => s.kind === kind);
    return !!slot && (slot.text !== null || slot.posture !== 'none');
  });
  return [...offered, ...kinds.filter(kind => !offered.includes(kind))].slice(0, ROW_DRAFT_SLOTS);
};

const offersDecisionDraft = (model: DraftModel): boolean =>
  model.slots.some(slot => isDraftDecisionKind(slot.kind) && (slot.text !== null || slot.posture === 'lazy'));

export interface TriageBarInput {
  status: string;
  model: DraftModel;
  face: FaceFn;
  maintenance: boolean;
  /** The host's own writes are on the wire or paused. */
  writesBlocked: boolean;
  /** A draft action (copy, edit, download) is on the wire — every draft act holds meanwhile. */
  draftBusy: boolean;
  /** The hold has lasted long enough to be WORN (dimmed, „Aktion läuft"); before that the held
   *  acts refuse at full opacity. */
  holdVisible?: boolean;
  canArchive: boolean;
  /** The letter shown above the bar (a row with two): its approval stands in slot 1. */
  shownKind?: DraftDecisionKind | null;
}

/** The triage flow's three slots for an open or decided row; null for any other status. */
export function triageBarSlots(i: TriageBarInput): BarSlots | null {
  const { status, model, face } = i;
  const open = status === 'suggestion_ready';
  if (!open && !isSettledStatus(status)) return null;
  const declined = status === 'declined';
  const blocked = i.writesBlocked || i.draftBusy;
  // A live act held only by a draft action in flight: refused at once, worn only after the delay.
  const held = (it: SlotItem): SlotItem => {
    if (!i.draftBusy || i.writesBlocked || it.face === 'potential' || it.title) return it;
    return i.holdVisible ? { ...it, title: BAR_REASON.busy } : { ...it, undimmed: true };
  };
  const slotOf = (kind: DraftSlotKind): DraftSlot =>
    model.slots.find(s => s.kind === kind)
    ?? { kind, posture: 'none', text: null, editedAt: null, scaffold: null, claim: null };

  const approveItem = (kind: DraftKind): SlotItem => held(approveItemRaw(kind));
  const approveItemRaw = (kind: DraftKind): SlotItem => {
    if (kind === 'archive') {
      return { label: draftKindActionLabel('archive', face), act: { type: 'approveKind', kind }, icon: 'check', enabled: !blocked, title: i.maintenance ? BAR_REASON.maintenance : undefined };
    }
    const slot = slotOf(kind as DraftSlotKind);
    const openFields = openSlotCount(slot);
    if (slot.text === null) {
      return { label: draftKindActionLabel(kind, face), act: { type: 'approveKind', kind }, icon: 'check', enabled: false, face: 'potential', title: slot.posture === 'lazy' ? BAR_REASON.notRendered : BAR_REASON.noDraft };
    }
    return {
      label: draftKindActionLabel(kind, face), act: { type: 'approveKind', kind }, icon: 'check',
      enabled: !blocked && openFields === 0,
      title: i.maintenance ? BAR_REASON.maintenance : openFields > 0 ? openSlotsNote(openFields) : undefined,
    };
  };
  const downloadItem = (kind: DraftDecisionKind): SlotItem => held(
    { label: draftKindDownloadLabel(kind, face), act: { type: 'downloadKind', kind }, icon: null, enabled: !i.draftBusy },
  );
  const withKeys = (items: SlotItem[]): SlotItem[] | null =>
    items.length >= 2 ? items.map((it, n) => ({ ...it, hotkey: String(n + 1) })) : null;

  const kinds = decisionKindsInModelOrder(model);
  const hasDraft = (kind: DraftDecisionKind) => slotOf(kind).text !== null;
  const approvedByApproval = (kind: DraftDecisionKind) => slotOf(kind).claim?.via === 'approve';
  const noOfferAtAll = model.shape !== 'legacy' && model.shape !== 'refusal' && !offersDecisionDraft(model);

  const retry = retrySlot(status, i.writesBlocked);
  const third: BarSlot = open ? { type: 'decline', noDraft: noOfferAtAll } : archiveSlot(i.canArchive, i.writesBlocked);

  if (declined) return [{ type: 'blank' }, retry, third];

  if (open) {
    if (noOfferAtAll) {
      return [{ type: 'act', main: approveItem('archive'), menu: null }, retry, third];
    }
    const listed = kinds.filter(hasDraft);
    // The letter the clerk is reading leads: the switch above the letter and slot 1 are one choice.
    const lead = i.shownKind && listed.includes(i.shownKind) ? i.shownKind
      : model.leadKind && isDraftDecisionKind(model.leadKind) && listed.includes(model.leadKind) ? model.leadKind : null;
    const ordered = lead ? [lead, ...listed.filter(k => k !== lead)] : listed;
    const items: SlotItem[] = [
      ...ordered.map(approveItem),
      ...(model.inReview ? [approveItem('archive')] : []),
    ];
    if (items.length === 0) {
      // Nothing composed yet and nothing to file: the KI's lead (or the first offered kind), washed.
      const first = (model.leadKind && isDraftDecisionKind(model.leadKind) ? model.leadKind : null) ?? kinds[0];
      if (!first) return [{ type: 'blank' }, retry, third];
      return [{ type: 'act', main: approveItem(first), menu: null }, retry, third];
    }
    const main = items[0];
    return [{ type: 'act', main, menu: withKeys(items), menuHeading: MENU_HEADING_APPROVE }, retry, third];
  }

  // Decided with a yes: the next open act on this mail, else the approved letters' downloads.
  const approvableAll = kinds.filter(k => hasDraft(k) && !approvedByApproval(k));
  const approvable = i.shownKind && approvableAll.includes(i.shownKind)
    ? [i.shownKind, ...approvableAll.filter(k => k !== i.shownKind)] : approvableAll;
  const downloads = kinds.filter(approvedByApproval);
  const items: SlotItem[] = [...approvable.map(approveItem), ...downloads.map(downloadItem)];
  if (items.length === 0) return [{ type: 'blank' }, retry, third];
  const main = items[0];
  const allApprove = approvable.length === items.length;
  return [{ type: 'act', main, menu: withKeys(items), menuHeading: allApprove ? MENU_HEADING_APPROVE : undefined }, retry, third];
}

/** The open slot-1 act the G key presses, when there is one to press. */
export const primaryApproveAct = (slots: BarSlots | null): SlotAct | null => {
  const s = slots?.[0];
  if (!s || s.type !== 'act' || !s.main.enabled) return null;
  return s.main.act.type === 'approve' || s.main.act.type === 'approveKind' ? s.main.act : null;
};

/** After a yes on an open WIM row, the next letter the card's A approves — slot 1's act when it is
 *  an approval of another kind. Null when there is none (a one-letter row, any letter-flow row). */
export const nextKindAct = (slots: BarSlots | null): { kind: DraftKind; label: string } | null => {
  const s = slots?.[0];
  if (!s || s.type !== 'act' || !s.main.enabled || s.main.act.type !== 'approveKind') return null;
  return { kind: s.main.act.kind, label: s.main.label };
};

/** Whether this ticket's bar is the triage one (a triage suggestion on an open or decided row). */
export const isTriageBar = (ticket: Pick<TicketDetail, 'status' | 'suggestion'>): boolean =>
  !!ticket.suggestion?.triage && (ticket.status === 'suggestion_ready' || isSettledStatus(ticket.status));

// ─── The columns ────────────────────────────────────────────────────────────────────────────────

/** Every face slot 1 can wear on this row, open or decided — so its column is sized once per row
 *  and never moves when a press changes the act. `caret`: the row can offer a list on slot 1. */
export interface SlotOneFaces { labels: { text: string; icon: boolean }[]; caret: boolean }

export const letterSlotOneFaces = (): SlotOneFaces => ({
  labels: [{ text: LETTER_APPROVE_LABEL, icon: true }, { text: LETTER_DOWNLOAD_LABEL, icon: false }],
  caret: false,
});

export const triageSlotOneFaces = (model: DraftModel, face: FaceFn): SlotOneFaces => {
  const kinds = decisionKindsInModelOrder(model);
  const offered = kinds.filter(k => {
    const s = model.slots.find(x => x.kind === k);
    return !!s && (s.text !== null || s.posture !== 'none');
  });
  return {
    labels: [
      ...kinds.flatMap(k => [{ text: draftKindActionLabel(k, face), icon: true }, { text: draftKindDownloadLabel(k, face), icon: false }]),
      { text: draftKindActionLabel('archive', face), icon: true },
    ],
    caret: offered.length >= 2 || model.inReview,
  };
};

/** The other two slots' faces (fixed German). */
export const SLOT_TWO_FACES = [{ text: RETRY_LABEL, icon: true }];
export const SLOT_THREE_FACES = [{ text: 'Nicht hilfreich', icon: true }, { text: ARCHIVE_LABEL, icon: false }];
